<!-- views/Training.vue -->
<!-- Phase 2: 練習ページ（Lazy Load版・モード・Day選択） -->

<template>
  <div class="training-page">
    <!-- ヘッダー -->
    <div class="page-header">
      <h1>🎤 発音練習</h1>
      <p>AIが発音を評価します。Perfect（100点）を目指しましょう！</p>
    </div>

    <!-- ローディング -->
    <div v-if="isLoading" class="loading-container">
      <div class="spinner"></div>
      <p>練習データを読み込み中...</p>
    </div>

    <!-- エラー表示 -->
    <div v-else-if="error" class="error-container">
      <h3>❌ エラーが発生しました</h3>
      <p>{{ error }}</p>
      <button @click="loadUnlockStatus" class="retry-button">再試行</button>
    </div>

    <!-- モード選択ドロップダウン -->
    <div v-else class="mode-selection-container">
      <label for="training-mode">練習モードを選択:</label>
      <select id="training-mode" v-model="selectedModeId" @change="onModeChange">
        <option value="" disabled>モードを選択してください</option>
        <option value="otoeigo">オトエイゴ</option>
        <option value="eiken">英検</option>
        <option value="esat-j">ESAT-J</option>
        <option value="fun">FUN</option>
      </select>
    </div>

    <!-- Day選択アコーディオン -->
    <div v-if="selectedModeId" class="day-accordion-container">
      <!-- オトエイゴモード: グループ化表示 -->
      <template v-if="selectedModeId === 'otoeigo'">
        <div 
          v-for="group in groupedDays" 
          :key="group.groupKey" 
          class="day-group"
        >
          <div 
            class="day-group-header" 
            @click="toggleGroup(group.groupKey)"
          >
            <h2>{{ group.groupLabel }}</h2>
            <span class="accordion-icon">{{ group.expanded ? '▲' : '▼' }}</span>
          </div>
          
          <div v-if="group.expanded" class="day-group-content">
            <div 
              v-for="day in group.days" 
              :key="day.dayNumber" 
              class="day-accordion-item"
              :class="{ 'locked': !day.unlocked }"
              :data-day-number="day.dayNumber"
            >
        <div class="day-accordion-header" @click="toggleDay(day.dayNumber)">
          <div class="day-header-content">
            <h3>Day {{ day.dayNumber }}</h3>
            <div v-if="day.unlocked && getDayStats(day.dayNumber)" class="day-stats">
              <span v-if="getDayStats(day.dayNumber).gold > 0" class="stat-badge gold">🥇{{ getDayStats(day.dayNumber).goldPercent }}%</span>
              <span v-if="getDayStats(day.dayNumber).silver > 0" class="stat-badge silver">🥈{{ getDayStats(day.dayNumber).silverPercent }}%</span>
              <span v-if="getDayStats(day.dayNumber).bronze > 0" class="stat-badge bronze">🥉{{ getDayStats(day.dayNumber).bronzePercent }}%</span>
              <span v-if="getDayStats(day.dayNumber).notDone > 0" class="stat-badge not-done">⚪{{ getDayStats(day.dayNumber).notDonePercent }}%</span>
            </div>
          </div>
          <div class="day-header-right">
            <span v-if="!day.unlocked" class="lock-badge">🔒 Locked</span>
            <span v-else class="unlock-badge">✅ Unlocked</span>
            <span class="accordion-icon">{{ day.expanded ? '▲' : '▼' }}</span>
          </div>
        </div>
        
        <!-- ローディング中 -->
        <div v-if="day.loading" class="day-accordion-content loading">
          <p>読み込み中...</p>
        </div>
        
        <!-- タスク一覧（アンロック済み） -->
        <div v-else-if="day.expanded && day.unlocked && day.tasks" class="day-accordion-content">
          <div class="task-list">
            <div 
              v-for="task in day.tasks" 
              :key="task.id"
              :class="['task-item', { 'task-highlight': focusedTaskId === task.id }]"
              :data-task-id="task.id"
            >
              <div class="task-content">
                <div class="task-text">
                  <p class="task-sentence">{{ task.sentence }}</p>
                  <p v-if="task.translation" class="task-translation">{{ task.translation }}</p>
                </div>
                <div class="task-actions">
                  <div v-if="getTaskStats(task.id)" class="task-score">
                    <span v-if="getTaskStats(task.id).rank" :class="['rank-badge', getTaskStats(task.id).rank]">
                      <span v-if="getTaskStats(task.id).rank === 'gold'">🥇</span>
                      <span v-else-if="getTaskStats(task.id).rank === 'silver'">🥈</span>
                      <span v-else-if="getTaskStats(task.id).rank === 'bronze'">🥉</span>
                      {{ getTaskStats(task.id).best_overall }}
                    </span>
                    <span v-else-if="getTaskStats(task.id).best_overall > 0" class="score-value">
                      {{ getTaskStats(task.id).best_overall }}
                    </span>
                  </div>
                  <button 
                    @click="startTask(task)"
                    class="practice-button"
                  >
                    🎤 練習開始
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- ロックメッセージ -->
        <div v-else-if="day.expanded && !day.unlocked" class="day-accordion-content locked-message">
          <p>前のDayの練習と課題を完了するとアンロックされます。</p>
        </div>
      </div>
          </div>
        </div>
      </template>
      
      <!-- その他のモード: 従来通り -->
      <template v-else>
        <div 
          v-for="day in availableDays" 
          :key="day.dayNumber" 
          class="day-accordion-item"
          :class="{ 'locked': !day.unlocked }"
          :data-day-number="day.dayNumber"
        >
          <div class="day-accordion-header" @click="toggleDay(day.dayNumber)">
            <div class="day-header-content">
              <h3>Day {{ day.dayNumber }}</h3>
              <div v-if="day.unlocked && getDayStats(day.dayNumber)" class="day-stats">
                <span v-if="getDayStats(day.dayNumber).gold > 0" class="stat-badge gold">🥇{{ getDayStats(day.dayNumber).goldPercent }}%</span>
                <span v-if="getDayStats(day.dayNumber).silver > 0" class="stat-badge silver">🥈{{ getDayStats(day.dayNumber).silverPercent }}%</span>
                <span v-if="getDayStats(day.dayNumber).bronze > 0" class="stat-badge bronze">🥉{{ getDayStats(day.dayNumber).bronzePercent }}%</span>
                <span v-if="getDayStats(day.dayNumber).notDone > 0" class="stat-badge not-done">⚪{{ getDayStats(day.dayNumber).notDonePercent }}%</span>
              </div>
            </div>
            <div class="day-header-right">
              <span v-if="!day.unlocked" class="lock-badge">🔒 Locked</span>
              <span v-else class="unlock-badge">✅ Unlocked</span>
              <span class="accordion-icon">{{ day.expanded ? '▲' : '▼' }}</span>
            </div>
          </div>
          
          <!-- ローディング中 -->
          <div v-if="day.loading" class="day-accordion-content loading">
            <p>読み込み中...</p>
          </div>
          
          <!-- タスク一覧（アンロック済み） -->
          <div v-else-if="day.expanded && day.unlocked && day.tasks" class="day-accordion-content">
            <div class="task-list">
            <div 
              v-for="task in day.tasks" 
              :key="task.id"
              :class="['task-item', { 'task-highlight': focusedTaskId === task.id }]"
              :data-task-id="task.id"
            >
                <div class="task-content">
                  <div class="task-text">
                    <p class="task-sentence">{{ task.sentence }}</p>
                    <p v-if="task.translation" class="task-translation">{{ task.translation }}</p>
                  </div>
                  <div class="task-actions">
                    <div v-if="getTaskStats(task.id)" class="task-score">
                      <span v-if="getTaskStats(task.id).rank" :class="['rank-badge', getTaskStats(task.id).rank]">
                        <span v-if="getTaskStats(task.id).rank === 'gold'">🥇</span>
                        <span v-else-if="getTaskStats(task.id).rank === 'silver'">🥈</span>
                        <span v-else-if="getTaskStats(task.id).rank === 'bronze'">🥉</span>
                        {{ getTaskStats(task.id).best_overall }}
                      </span>
                      <span v-else-if="getTaskStats(task.id).best_overall > 0" class="score-value">
                        {{ getTaskStats(task.id).best_overall }}
                      </span>
                    </div>
                    <button 
                      @click="startTask(task)"
                      class="practice-button"
                    >
                      🎤 練習開始
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- ロックメッセージ -->
          <div v-else-if="day.expanded && !day.unlocked" class="day-accordion-content locked-message">
            <p>前のDayの練習と課題を完了するとアンロックされます。</p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, shallowRef, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { supabase } from '../supabaseClient'

const router = useRouter()
const route = useRoute()

// 状態
const isLoading = ref(true)
const error = ref(null)
const selectedModeId = ref('otoeigo')
const selectedLevel = ref(null) // 英検/ESAT-J用のレベル選択
const days = ref([])

// Lazy Load用のMap管理（day別タスクキャッシュ）
const tasksByDay = shallowRef(new Map())   // key: `${mode}:${level||''}:${day}`
const isLoadingDay = shallowRef(new Set()) // ローディング管理
const expandedDays = ref(new Set())
const expandedGroups = ref(new Set()) // グループの展開状態を管理
const doneMap = ref(null) // 高速アンロック判定用キャッシュ（Map形式）
const pendingDayToOpen = ref(null)
const pendingTaskToFocus = ref(null)
const focusedTaskId = ref(null)
const loadedGroupKeys = ref(new Set())
const loadingGroups = shallowRef(new Set())

const DAY_GROUPS = Array.from({ length: 18 }, (_, index) => {
  const start = index * 10 + 1
  const end = Math.min(start + 9, 180)
  return {
    start,
    end,
    key: `day${start}-${end}`,
    label: `Day ${start}-${end}`
  }
})

const getGroupByDayNumber = (dayNumber) => {
  return DAY_GROUPS.find(group => dayNumber >= group.start && dayNumber <= group.end)
}

const getGroupByKey = (groupKey) => {
  return DAY_GROUPS.find(group => group.key === groupKey)
}

// 統計データ管理
const dayStatsMap = ref(new Map())  // key: dayNumber, value: { gold, silver, bronze, notDone, total }
const taskStatsMap = ref(new Map()) // key: taskId, value: { best_overall, rank }

const getQueryValue = (value) => Array.isArray(value) ? value[0] : value

const applyRouteQueryState = () => {
  const queryMode = getQueryValue(route.query.mode)
  if (typeof queryMode === 'string' && queryMode) {
    selectedModeId.value = queryMode
  }
  const queryLevel = getQueryValue(route.query.level)
  if (typeof queryLevel === 'string' && queryLevel) {
    selectedLevel.value = queryLevel
  }
  const queryDay = Number(getQueryValue(route.query.day))
  pendingDayToOpen.value = Number.isFinite(queryDay) && queryDay > 0 ? queryDay : null
  const queryTaskId = getQueryValue(route.query.taskId)
  pendingTaskToFocus.value = typeof queryTaskId === 'string' && queryTaskId ? queryTaskId : null
}

applyRouteQueryState()

// key生成ヘルパー
const keyFor = (dayNumber) => `${selectedModeId.value}:${selectedLevel.value || ''}:${dayNumber}`

const enrichDaysForCurrentMode = () => {
  return days.value
    .filter(day => day.mode === selectedModeId.value)
    .map(day => {
      const unlocked = isDayUnlocked(day.dayNumber)
      return {
        ...day,
        tasks: getTasksForDay(day.dayNumber),
        unlocked,
        expanded: expandedDays.value.has(day.dayNumber),
        loading: isLoadingDay.value.has(day.dayNumber)
      }
    })
}

const groupedDays = computed(() => {
  if (selectedModeId.value !== 'otoeigo') return []
  const enrichedDays = enrichDaysForCurrentMode()
  return DAY_GROUPS.map(group => {
    const groupDays = enrichedDays.filter(day => day.dayNumber >= group.start && day.dayNumber <= group.end)
    return {
      groupKey: group.key,
      groupLabel: group.label,
      days: groupDays,
      expanded: expandedGroups.value.has(group.key),
      loading: loadingGroups.value.has(group.key)
    }
  })
})

const availableDays = computed(() => {
  if (!selectedModeId.value) return []
  if (selectedModeId.value === 'otoeigo') {
    return groupedDays.value
  }
  return enrichDaysForCurrentMode().map(day => ({
    groupKey: `day${day.dayNumber}`,
    groupLabel: null,
    days: [day],
    expanded: true,
    loading: false
  }))
})

// Day統計を取得（パーセンテージ付き）
const getDayStats = (dayNumber) => {
  const stats = dayStatsMap.value.get(dayNumber)
  if (!stats || stats.total === 0) return null
  
  return {
    ...stats,
    goldPercent: Math.round((stats.gold / stats.total) * 100),
    silverPercent: Math.round((stats.silver / stats.total) * 100),
    bronzePercent: Math.round((stats.bronze / stats.total) * 100),
    notDonePercent: Math.round((stats.notDone / stats.total) * 100)
  }
}

// 課題統計を取得
const getTaskStats = (taskId) => {
  return taskStatsMap.value.get(taskId) || null
}

// Dayのタスクを取得（Lazy Load対応）
const getTasksForDay = (dayNumber) => tasksByDay.value.get(keyFor(dayNumber)) ?? []

// 高速アンロック判定ロジック（Map使用）
const loadUnlockStatus = async () => {
  if (doneMap.value) return doneMap.value // キャッシュヒット
  
  try {
    const user = (await supabase.auth.getUser()).data.user
    
    // submissions_v3 取得は一度だけ（必要なフィールドのみ）
    const { data: subs } = await supabase
      .from('submissions_v3')
      .select('day, type')
      .eq('user_id', user.id)
      .in('type', ['practice', 'assignment'])
    
    // dayごと集計（O(1)参照用のMap構築）
    const done = new Map()
    for (const s of subs ?? []) {
      const row = done.get(s.day) ?? { practice: false, assignment: false }
      if (s.type === 'practice') row.practice = true
      if (s.type === 'assignment') row.assignment = true
      done.set(s.day, row)
    }
    
    doneMap.value = done
    return done
    
  } catch (err) {
    console.error('アンロック状況読み込み失敗:', err)
    error.value = 'アンロック状況の読み込みに失敗しました'
    return doneMap.value
  }
}

const isDayUnlocked = (dayNumber) => {
  // オトエイゴモードのみアンロック機能あり
  if (selectedModeId.value !== 'otoeigo') return true
  
  // Day1は常時開放
  if (dayNumber === 1) return true
  
  // DayNは Day(N-1) の practice & assignment 完了で開放
  if (!doneMap.value) return false // まだロードされていない場合は false
  const prev = doneMap.value.get(dayNumber - 1)
  return !!prev?.practice && !!prev?.assignment
}

const loadDayStatsForDays = async (targetDayNumbers = []) => {
  try {
    const pendingDayNumbers = targetDayNumbers.filter(dayNumber => !dayStatsMap.value.has(dayNumber))
    if (pendingDayNumbers.length === 0) return
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    
    const statsPromises = pendingDayNumbers.map(async (dayNumber) => {
      const { data, error } = await supabase.rpc('get_day_stats_summary', {
        p_user_id: user.id,
        p_day: dayNumber,
        p_mode: selectedModeId.value,
        p_level: selectedLevel.value
      })
      
      if (error) {
        console.error(`Day ${dayNumber} 統計取得失敗:`, error)
        return { dayNumber, stats: null }
      }
      
      return {
        dayNumber,
        stats: data && data.length > 0 ? data[0] : { gold_count: 0, silver_count: 0, bronze_count: 0, not_done_count: 0, total_count: 0 }
      }
    })
    
    const results = await Promise.all(statsPromises)
    const newMap = new Map(dayStatsMap.value)
    
    for (const { dayNumber, stats } of results) {
      if (stats) {
        newMap.set(dayNumber, {
          gold: stats.gold_count,
          silver: stats.silver_count,
          bronze: stats.bronze_count,
          notDone: stats.not_done_count,
          total: stats.total_count
        })
      }
    }
    
    dayStatsMap.value = newMap
    console.log('✅ Day統計取得完了:', { added: pendingDayNumbers.length })
  } catch (error) {
    console.error('Day統計取得エラー:', error)
  }
}

const upsertDaysForCurrentMode = (dayNumbers = []) => {
  if (!Array.isArray(dayNumbers) || dayNumbers.length === 0) return
  const existingDayNumbers = new Set(
    days.value
      .filter(day => day.mode === selectedModeId.value)
      .map(day => day.dayNumber)
  )
  const newEntries = dayNumbers
    .filter(dayNumber => !existingDayNumbers.has(dayNumber))
    .map(dayNumber => ({ dayNumber, mode: selectedModeId.value }))
  if (newEntries.length === 0) return
  days.value = [...days.value, ...newEntries].sort((a, b) => a.dayNumber - b.dayNumber)
}

const resetTrainingData = () => {
  days.value = []
  tasksByDay.value = new Map()
  isLoadingDay.value = new Set()
  expandedDays.value = new Set()
  expandedGroups.value = new Set()
  dayStatsMap.value = new Map()
  taskStatsMap.value = new Map()
  loadedGroupKeys.value = new Set()
  loadingGroups.value = new Set()
}

const loadAllDaysForMode = async () => {
  try {
    let q = supabase
      .from('training_tasks')
      .select('day_number')
      .eq('mode', selectedModeId.value)
      .order('day_number', { ascending: true })
    if (selectedLevel.value) {
      q = q.eq('level', selectedLevel.value)
    }
    const { data, error } = await q
    if (error) {
      console.error('Day一覧取得失敗:', error)
      return
    }
    const dayNumbers = Array.from(new Set(data?.map(r => r.day_number) || []))
    upsertDaysForCurrentMode(dayNumbers)
    await loadDayStatsForDays(dayNumbers)
  } catch (error) {
    console.error('Day一覧読み込みエラー:', error)
  }
}

const ensureGroupDataLoaded = async (groupKey) => {
  const group = getGroupByKey(groupKey)
  if (!group) return
  if (loadedGroupKeys.value.has(groupKey)) return
  loadingGroups.value.add(groupKey)
  try {
    let q = supabase
      .from('training_tasks')
      .select('day_number')
      .eq('mode', selectedModeId.value)
      .gte('day_number', group.start)
      .lte('day_number', group.end)
      .order('day_number', { ascending: true })
    if (selectedLevel.value) {
      q = q.eq('level', selectedLevel.value)
    }
    const { data, error } = await q
    if (error) {
      console.error(`Day一覧取得失敗 (${group.label}):`, error)
      return
    }
    const dayNumbers = Array.from(new Set(data?.map(r => r.day_number) || []))
    upsertDaysForCurrentMode(dayNumbers)
    loadedGroupKeys.value = new Set(loadedGroupKeys.value).add(groupKey)
    await loadDayStatsForDays(dayNumbers)
  } catch (error) {
    console.error(`Day一覧読み込みエラー (${groupKey}):`, error)
  } finally {
    const nextLoading = new Set(loadingGroups.value)
    nextLoading.delete(groupKey)
    loadingGroups.value = nextLoading
  }
}

// 特定Dayの課題統計を一括取得
const loadTaskStatsBatch = async (dayNumber) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    
    const k = keyFor(dayNumber)
    const tasks = tasksByDay.value.get(k) ?? []
    if (tasks.length === 0) return
    
    const taskIds = tasks.map(t => t.id)
    
    const { data, error } = await supabase.rpc('get_tasks_stats_batch', {
      p_user_id: user.id,
      p_task_ids: taskIds
    })
    
    if (error) {
      console.error(`Day ${dayNumber} 課題統計取得失敗:`, error)
      return
    }
    
    // 各課題のランクを計算してMapに保存
    const newMap = new Map(taskStatsMap.value)
    
    for (const task of tasks) {
      const stats = data?.find(s => s.task_id === task.id)
      const bestOverall = stats?.best_overall ?? 0
      
      // ランクを計算（タスク固有の閾値が無い場合はデフォルト）
      const thresholds = {
        gold: 95,
        silver: 90,
        bronze: 85
      }
      
      let rank = null
      if (bestOverall > 0) {
        if (bestOverall >= thresholds.gold) {
          rank = 'gold'
        } else if (bestOverall >= thresholds.silver) {
          rank = 'silver'
        } else if (bestOverall >= thresholds.bronze) {
          rank = 'bronze'
        }
      }
      
      newMap.set(task.id, {
        best_overall: bestOverall,
        rank
      })
    }
    
    taskStatsMap.value = newMap
    console.log(`✅ Day ${dayNumber} 課題統計取得完了:`, { count: tasks.length })
  } catch (error) {
    console.error(`Day ${dayNumber} 課題統計取得エラー:`, error)
  }
}

// 展開時フェッチ：そのDayだけLazy Load
const ensureTasksLoaded = async (dayNumber) => {
  const k = keyFor(dayNumber)
  if (tasksByDay.value.has(k)) {
    // キャッシュヒット時も統計が未取得なら取得
    if (!taskStatsMap.value.has(Array.from(tasksByDay.value.get(k) ?? [])[0]?.id)) {
      await loadTaskStatsBatch(dayNumber)
    }
    return
  }
  
  isLoadingDay.value.add(dayNumber)
  
  let q = supabase
    .from('training_tasks')
    .select('id, sentence, translation, audio_ref, task_order, mode, day_number, difficulty')
    .eq('mode', selectedModeId.value)
    .eq('day_number', dayNumber)
    .order('task_order', { ascending: true })
  
  // levelがあるモードなら追加
  if (selectedLevel.value) {
    q = q.eq('level', selectedLevel.value)
  }
  
  const { data, error } = await q
  
  if (error) {
    console.error('タスク取得失敗:', error)
    isLoadingDay.value.delete(dayNumber)
    return
  }
  
  // Mapにキャッシュ
  const m = new Map(tasksByDay.value)
  m.set(k, data ?? [])
  tasksByDay.value = m
  
  isLoadingDay.value.delete(dayNumber)
  
  // タスク読み込み後に統計も取得
  await loadTaskStatsBatch(dayNumber)
}

// グループのトグル
const toggleGroup = async (groupKey) => {
  const isExpanded = expandedGroups.value.has(groupKey)
  if (isExpanded) {
    expandedGroups.value = new Set()
    return
  }
  expandedGroups.value = new Set([groupKey])
  await ensureGroupDataLoaded(groupKey)
}

// トグル時にだけ読み込む（Lazy Load）
const toggleDay = async (dayNumber) => {
  const isExpanded = expandedDays.value.has(dayNumber)
  if (isExpanded) {
    expandedDays.value = new Set()
    return
  }
  expandedDays.value = new Set([dayNumber])
  
  if (isDayUnlocked(dayNumber)) {
    await ensureTasksLoaded(dayNumber) // ← ここで初めて取得
  }

  await nextTick()
  const dayElement = document.querySelector(`[data-day-number="${dayNumber}"]`)
  if (dayElement) {
    dayElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const focusTaskElement = async (taskId) => {
  await nextTick()
  return new Promise((resolve) => {
    const attempt = (retries = 5) => {
      const el = document.querySelector(`[data-task-id="${taskId}"]`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        resolve()
      } else if (retries > 0) {
        requestAnimationFrame(() => attempt(retries - 1))
      } else {
        resolve()
      }
    }
    attempt()
  })
}

const expandDayFromRoute = async (dayNumber, taskId = null) => {
  const target = Number(dayNumber)
  if (!Number.isFinite(target) || target <= 0) return

  if (selectedModeId.value === 'otoeigo') {
    const group = getGroupByDayNumber(target)
    if (group) {
      if (!expandedGroups.value.has(group.key)) {
        expandedGroups.value = new Set([group.key])
      }
      await ensureGroupDataLoaded(group.key)
    }
  }

  if (!expandedDays.value.has(target)) {
    const nextDays = new Set(expandedDays.value)
    nextDays.add(target)
    expandedDays.value = nextDays
  }

  if (isDayUnlocked(target)) {
    await ensureTasksLoaded(target)
  }

  await nextTick()
  const dayElement = document.querySelector(`[data-day-number="${target}"]`)
  if (dayElement) {
    dayElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (taskId) {
    focusedTaskId.value = taskId
    await focusTaskElement(taskId)
  } else {
    focusedTaskId.value = null
  }
}

// メソッド
const onModeChange = async () => {
  console.log('モード選択:', selectedModeId.value)
  
  // 初期化
  resetTrainingData()
  await loadUnlockStatus()
  if (selectedModeId.value !== 'otoeigo') {
    await loadAllDaysForMode()
  }
}

const startTask = (task) => {
  // タスク詳細ページへ遷移（dayId, mode, level, taskIdをクエリパラメータとして渡す）
  console.log('タスク開始:', task)
  
  const query = {
    day: task.day_number,
    index: task.task_order - 1,  // 0ベースのインデックス
    mode: selectedModeId.value    // modeを追加
  }
  
  // levelがあるモード（英検、ESAT-J）の場合はlevelでも追加
  if (selectedLevel.value) {
    query.level = selectedLevel.value
  }
  query.taskId = task.id
  
  router.push({
    name: 'TrainingTask',
    params: { taskId: String(task.id) },
    query
  })
}

// 更新ボタン（F5）の挙動をナビゲーションバーの「発音練習」ボタンと同じにする
const handlePageShow = (event) => {
  // bfcacheから復元された場合（通常のリロードではない）は何もしない
  if (event.persisted) {
    return
  }
  // 通常のリロードの場合、/trainingに単純に遷移（ナビゲーションバーの「発音練習」ボタンと同じ挙動）
  // クエリパラメータをクリアして初期状態に戻す
  router.replace('/training')
}

// ライフサイクル
onMounted(async () => {
  // 高速アンロック判定用データを事前ロード（Map構築）
  await loadUnlockStatus()
  resetTrainingData()
  if (selectedModeId.value !== 'otoeigo') {
    await loadAllDaysForMode()
  }
  if (pendingDayToOpen.value) {
    await expandDayFromRoute(pendingDayToOpen.value, pendingTaskToFocus.value)
    pendingDayToOpen.value = null
    pendingTaskToFocus.value = null
  }
  isLoading.value = false
  
  // 更新ボタン（F5）のイベントリスナーを登録
  window.addEventListener('pageshow', handlePageShow)
})

onUnmounted(() => {
  // イベントリスナーをクリーンアップ
  window.removeEventListener('pageshow', handlePageShow)
})
</script>

<style scoped>
.training-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
}

.page-header {
  text-align: center;
  margin-bottom: 3rem;
  color: white;
}

.page-header h1 {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.page-header p {
  font-size: 1.1rem;
  opacity: 0.9;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: white;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container {
  text-align: center;
  color: white;
  background: rgba(239, 68, 68, 0.1);
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.retry-button {
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 1rem;
}

.retry-button:hover {
  background: #dc2626;
}

.mode-selection-container {
  max-width: 800px;
  margin: 2rem auto;
  text-align: center;
}

.mode-selection-container label {
  color: white;
  font-size: 1.2rem;
  display: block;
  margin-bottom: 1rem;
}

.mode-selection-container select {
  padding: 1rem 2rem;
  border-radius: 8px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  appearance: none;
  background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23ffffff%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.9%205.4s9.3-1.8%2012.9-5.4l128-127.9c3.6-3.6%205.4-7.8%205.4-12.9%200-5-1.8-9.3-5.4-12.9z%22%2F%3E%3C%2Fsvg%3E');
  background-repeat: no-repeat;
  background-position: right 1rem top 50%, 0 0;
  background-size: 0.65em auto, 100%;
}

.mode-selection-container select:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.5);
}

/* ドロップダウン展開時の可読性確保（白背景に黒文字） */
.mode-selection-container select option,
.mode-selection-container select optgroup {
  color: #111;
  background: #fff;
}

.day-accordion-container {
  max-width: 800px;
  margin: 2rem auto;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

/* グループ化スタイル（オトエイゴモード用） */
.day-group {
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 1rem;
}

.day-group:last-child {
  border-bottom: none;
}

.day-group-header {
  background: rgba(99, 102, 241, 0.2);
  padding: 1rem 1.5rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s;
}

.day-group-header:hover {
  background: rgba(99, 102, 241, 0.3);
}

.day-group-header h2 {
  margin: 0;
  color: white;
  font-size: 1.3rem;
  font-weight: 600;
}

.day-group-content {
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
}

.day-accordion-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.day-accordion-item:last-child {
  border-bottom: none;
}

.day-accordion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  cursor: pointer;
  color: white;
  font-size: 1.2rem;
  font-weight: bold;
  transition: background-color 0.2s;
}

.day-header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.day-header-content h3 {
  margin: 0;
}

.day-stats {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.stat-badge {
  font-size: 0.85rem;
  padding: 0.25rem 0.6rem;
  border-radius: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.stat-badge.gold {
  background: rgba(255, 215, 0, 0.2);
  border: 1px solid rgba(255, 215, 0, 0.4);
  color: #ffd700;
}

.stat-badge.silver {
  background: rgba(192, 192, 192, 0.2);
  border: 1px solid rgba(192, 192, 192, 0.4);
  color: #c0c0c0;
}

.stat-badge.bronze {
  background: rgba(205, 127, 50, 0.2);
  border: 1px solid rgba(205, 127, 50, 0.4);
  color: #cd7f32;
}

.stat-badge.not-done {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.7);
}

.day-header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.day-accordion-item.locked .day-accordion-header {
  background-color: rgba(255, 255, 255, 0.05);
  cursor: not-allowed;
  opacity: 0.6;
}

.day-accordion-header:hover:not(.day-accordion-item.locked .day-accordion-header) {
  background-color: rgba(255, 255, 255, 0.15);
}


.lock-badge, .unlock-badge {
  font-size: 0.8rem;
  padding: 0.3rem 0.8rem;
  border-radius: 8px;
  margin-right: 1rem;
}

.lock-badge {
  background-color: #ef4444;
  color: white;
}

.unlock-badge {
  background-color: #22c55e;
  color: white;
}

.accordion-icon {
  font-size: 0.9rem;
  margin-left: 0.5rem;
}

.day-accordion-content {
  padding: 1.5rem 2rem;
  background-color: rgba(0, 0, 0, 0.1);
  color: white;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.day-accordion-content.loading {
  text-align: center;
  font-style: italic;
  color: rgba(255, 255, 255, 0.7);
}

.day-accordion-content.locked-message {
  text-align: center;
  font-style: italic;
  color: rgba(255, 255, 255, 0.7);
}

.task-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.task-item {
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  border-radius: 8px;
}

.task-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.task-text {
  flex: 1;
  min-width: 0;
}

.task-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.task-score {
  display: flex;
  align-items: center;
}

.rank-badge {
  font-size: 0.9rem;
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  font-weight: 600;
  white-space: nowrap;
}

.rank-badge.gold {
  background: rgba(255, 215, 0, 0.2);
  border: 1px solid rgba(255, 215, 0, 0.4);
  color: #ffd700;
}

.rank-badge.silver {
  background: rgba(192, 192, 192, 0.2);
  border: 1px solid rgba(192, 192, 192, 0.4);
  color: #c0c0c0;
}

.rank-badge.bronze {
  background: rgba(205, 127, 50, 0.2);
  border: 1px solid rgba(205, 127, 50, 0.4);
  color: #cd7f32;
}

.score-value {
  font-size: 0.9rem;
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-weight: 600;
}

.task-sentence {
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}
.task-highlight {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.6);
  animation: highlight-pulse 1.2s ease;
}

@keyframes highlight-pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.02);
    opacity: 0.9;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.task-translation {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1rem;
}

.practice-button {
  background: #3b82f6;
  color: white;
  border: none;
  padding: clamp(0.5rem, 2vw, 0.75rem) clamp(1rem, 4vw, 1.25rem);
  border-radius: 6px;
  cursor: pointer;
  font-size: clamp(0.85rem, 3vw, 1rem);
  transition: all 0.2s;
  white-space: nowrap;
  min-width: 6rem;
}

.practice-button:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

@media (max-width: 768px) {
  .training-page {
    padding: 1rem;
  }
  
  .page-header h1 {
    font-size: 2rem;
  }
  
  .day-accordion-header {
    padding: 1rem 1.5rem;
    font-size: 1rem;
  }
  
  .task-item {
    padding: 0.75rem;
  }
  
  .task-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .task-actions {
    width: 100%;
    justify-content: space-between;
  }
  
  .day-header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .day-stats {
    font-size: 0.75rem;
  }
  
  .stat-badge {
    font-size: 0.7rem;
    padding: 0.2rem 0.5rem;
  }
}
</style>
